/**
 * Road connectivity validator. Pure-logic graph build + reachability + cluster
 * grouping for road-needing buildings. The town hall is the root; any road
 * tile cardinally adjacent to it seeds the BFS. A building is "connected" iff
 * any of its perimeter squares contains a reached road tile.
 *
 * Permissive contract (Phase 2): disconnected buildings are reported, never
 * thrown. The plan stays editable; the renderer tints offending buildings red
 * via `invalidBuildingIds`.
 *
 * Cluster grouping maps the player's mental model: "I have three places where
 * things broke" rather than "I have twelve broken things." Two disconnected
 * buildings share a cluster iff a chain of unreached road tiles links their
 * perimeters. Buildings with no roads on their perimeter at all are their own
 * singleton clusters.
 *
 * The flag fields (`isRoad`, `isMainBuilding`, `needsRoad`) come pre-derived
 * from the catalogue resolver — see `lib/catalogue/normalize.ts`. Don't
 * re-derive them here.
 */
import type { GridCoord, Plan, PlanBuilding, ResolvedBuilding } from '$lib/types'
import type { CatalogueLookup, ValidationError } from './validation'

export interface ConnectivityResult {
  /** Each inner array is one cluster's disconnected building ids. */
  disconnectedClusters: number[][]
  /** Sum of inner-array lengths. Convenience for the banner's count. */
  totalDisconnected: number
}

/**
 * Module-local memo keyed by `(planId, version)`. Connectivity is the most
 * expensive validator and is recomputed only when a command bumps the plan's
 * in-memory version. The composite key is robust against cross-plan
 * collisions (two freshly-loaded plans both at version 0 hit the same key
 * if we used version alone).
 *
 * Note: the memo doesn't track the `lookup` reference. The catalogue is
 * stable per session per-import; if a test passes different lookups for the
 * same (planId, version), the memo will return stale data — call
 * `clearConnectivityMemo()` in `beforeEach`.
 */
let memo: { planId: number; version: number; errors: ValidationError[] } | null = null

/** Test-only: drop the memo. Production code never needs to call this. */
export function clearConnectivityMemo(): void {
  memo = null
}

interface ConnectivityInputs {
  /** Squares occupied by at least one road tile. */
  roadSquares: Set<string>
  /** Squares occupied by the (chosen) town hall. Empty if absent. */
  townHallSquares: Set<string>
  /** Buildings flagged `needsRoad === true` and not themselves a road / town hall. */
  roadNeedingBuildings: Array<{
    id: number
    perimeterSquares: GridCoord[]
  }>
}

export function checkConnectivity(plan: Plan, lookup: CatalogueLookup): ValidationError[] {
  if (memo && memo.planId === plan.id && memo.version === plan.version) {
    return memo.errors
  }
  const result = computeConnectivity(plan, lookup)
  const errors = wrapAsErrors(result)
  memo = { planId: plan.id, version: plan.version, errors }
  return errors
}

export function computeConnectivity(
  plan: Plan,
  lookup: CatalogueLookup,
): ConnectivityResult {
  const inputs = collectInputs(plan, lookup)

  // Hot-path: no town hall → every road-needing building reports as one
  // single cluster. Skip the BFS — no root, no point building a road graph.
  if (inputs.townHallSquares.size === 0) {
    if (inputs.roadNeedingBuildings.length === 0) {
      return { disconnectedClusters: [], totalDisconnected: 0 }
    }
    const cluster = inputs.roadNeedingBuildings.map((b) => b.id)
    return { disconnectedClusters: [cluster], totalDisconnected: cluster.length }
  }

  if (inputs.roadNeedingBuildings.length === 0) {
    return { disconnectedClusters: [], totalDisconnected: 0 }
  }

  const reached = reachableRoadSquares(inputs)

  const disconnected: Array<{ id: number; perimeterSquares: GridCoord[] }> = []
  for (const b of inputs.roadNeedingBuildings) {
    const isConnected = b.perimeterSquares.some((sq) => reached.has(tileKey(sq.x, sq.y)))
    if (!isConnected) disconnected.push(b)
  }

  if (disconnected.length === 0) {
    return { disconnectedClusters: [], totalDisconnected: 0 }
  }

  return groupDisconnectedIntoClusters(disconnected, inputs.roadSquares, reached)
}

function collectInputs(plan: Plan, lookup: CatalogueLookup): ConnectivityInputs {
  const roadSquares = new Set<string>()
  const townHallCandidates: Array<{ building: PlanBuilding; resolved: ResolvedBuilding }> = []
  const roadNeeding: Array<{ building: PlanBuilding; resolved: ResolvedBuilding }> = []

  for (const building of plan.buildings) {
    const resolved = lookup(building.cityentityId)
    if (!resolved || resolved.isOffGrid) continue

    if (resolved.isRoad) {
      const w = resolved.width > 0 ? resolved.width : 1
      const h = resolved.height > 0 ? resolved.height : 1
      for (let dx = 0; dx < w; dx++) {
        for (let dy = 0; dy < h; dy++) {
          roadSquares.add(tileKey(building.x + dx, building.y + dy))
        }
      }
      continue
    }

    if (resolved.isMainBuilding) {
      townHallCandidates.push({ building, resolved })
      continue
    }

    if (resolved.needsRoad) {
      roadNeeding.push({ building, resolved })
    }
  }

  // Multiple town halls is editor mucking-about (legitimate FoE state has
  // exactly one). Pick the lowest id deterministically and treat the rest as
  // road-needing — they don't get a free pass just because their flag claims
  // main_building. Validation never throws, so we don't error on this state.
  const townHallSquares = new Set<string>()
  if (townHallCandidates.length > 0) {
    townHallCandidates.sort((a, b) => a.building.id - b.building.id)
    const chosen = townHallCandidates[0]
    const w = chosen.resolved.width > 0 ? chosen.resolved.width : 1
    const h = chosen.resolved.height > 0 ? chosen.resolved.height : 1
    for (let dx = 0; dx < w; dx++) {
      for (let dy = 0; dy < h; dy++) {
        townHallSquares.add(tileKey(chosen.building.x + dx, chosen.building.y + dy))
      }
    }
    // Treat extra town halls as road-needing buildings so a duplicate town
    // hall away from the main road shows up as disconnected rather than
    // silently passing.
    for (let i = 1; i < townHallCandidates.length; i++) {
      roadNeeding.push(townHallCandidates[i])
    }
  }

  const roadNeedingBuildings = roadNeeding.map(({ building, resolved }) => ({
    id: building.id,
    perimeterSquares: perimeterOf(building, resolved),
  }))

  return { roadSquares, townHallSquares, roadNeedingBuildings }
}

/**
 * Cardinal-adjacent squares immediately outside a building's footprint.
 * A 2×3 has 10 perimeter squares (top: 2, bottom: 2, left: 3, right: 3).
 * Corners are NOT included — FoE's road connectivity is 4-way, not 8-way.
 */
function perimeterOf(building: PlanBuilding, resolved: ResolvedBuilding): GridCoord[] {
  const w = resolved.width > 0 ? resolved.width : 1
  const h = resolved.height > 0 ? resolved.height : 1
  const out: GridCoord[] = []
  // Top + bottom rows (excluding corners).
  for (let dx = 0; dx < w; dx++) {
    out.push({ x: building.x + dx, y: building.y - 1 })
    out.push({ x: building.x + dx, y: building.y + h })
  }
  // Left + right columns (excluding corners).
  for (let dy = 0; dy < h; dy++) {
    out.push({ x: building.x - 1, y: building.y + dy })
    out.push({ x: building.x + w, y: building.y + dy })
  }
  return out
}

function reachableRoadSquares(inputs: ConnectivityInputs): Set<string> {
  // Seeds: every road tile cardinally adjacent to ANY town hall square. The
  // town hall itself isn't a road; its perimeter may or may not contain
  // roads. BFS the road graph from those seeds outward, 4-way adjacency.
  const seeds: string[] = []
  for (const sq of inputs.townHallSquares) {
    for (const n of cardinalNeighbours(sq)) {
      if (inputs.roadSquares.has(n)) seeds.push(n)
    }
  }
  const reached = new Set<string>(seeds)
  const queue = [...seeds]
  while (queue.length > 0) {
    const sq = queue.shift()!
    for (const n of cardinalNeighbours(sq)) {
      if (inputs.roadSquares.has(n) && !reached.has(n)) {
        reached.add(n)
        queue.push(n)
      }
    }
  }
  return reached
}

function groupDisconnectedIntoClusters(
  disconnected: Array<{ id: number; perimeterSquares: GridCoord[] }>,
  roadSquares: Set<string>,
  reached: Set<string>,
): ConnectivityResult {
  // Flood-fill the unreached road squares to identify connected components.
  // Each disconnected building maps to the component touching its perimeter;
  // buildings with no perimeter roads at all become their own singleton.
  const unreached = new Set<string>()
  for (const sq of roadSquares) if (!reached.has(sq)) unreached.add(sq)

  const componentOf = new Map<string, number>()
  let nextComponent = 0
  for (const sq of unreached) {
    if (componentOf.has(sq)) continue
    const compId = nextComponent++
    componentOf.set(sq, compId)
    const queue = [sq]
    while (queue.length > 0) {
      const cur = queue.shift()!
      for (const n of cardinalNeighbours(cur)) {
        if (unreached.has(n) && !componentOf.has(n)) {
          componentOf.set(n, compId)
          queue.push(n)
        }
      }
    }
  }

  // Assign each disconnected building to a cluster keyed by the first
  // unreached-road component its perimeter touches. Buildings with no
  // perimeter roads get unique synthetic ids so they remain singletons.
  const clusters = new Map<number | string, number[]>()
  let nextSingleton = 0
  for (const b of disconnected) {
    let key: number | string | null = null
    for (const sq of b.perimeterSquares) {
      const compId = componentOf.get(tileKey(sq.x, sq.y))
      if (compId !== undefined) {
        key = compId
        break
      }
    }
    if (key === null) key = `s${nextSingleton++}`
    const list = clusters.get(key)
    if (list) list.push(b.id)
    else clusters.set(key, [b.id])
  }

  // Order clusters by their first building's id so the banner's [show] cycle
  // is deterministic; buildings inside each cluster also id-sorted.
  const ordered = Array.from(clusters.values()).map((c) => [...c].sort((a, b) => a - b))
  ordered.sort((a, b) => a[0] - b[0])

  return {
    disconnectedClusters: ordered,
    totalDisconnected: disconnected.length,
  }
}

function wrapAsErrors(result: ConnectivityResult): ValidationError[] {
  return result.disconnectedClusters.map((cluster) => ({
    code: 'disconnected' as const,
    buildingIds: cluster,
    // The renderer's red tint comes from invalidBuildingIds; per-square
    // squares aren't load-bearing for connectivity errors.
    squares: [],
  }))
}

function tileKey(x: number, y: number): string {
  return `${x},${y}`
}

function cardinalNeighbours(key: string): string[] {
  const [xs, ys] = key.split(',')
  const x = Number(xs)
  const y = Number(ys)
  return [tileKey(x + 1, y), tileKey(x - 1, y), tileKey(x, y + 1), tileKey(x, y - 1)]
}
